import React, { useMemo, useState } from "react";
import {
  Breadcrumb,
  Button,
  InputNumber,
  Space,
  Table,
  Tag,
  Divider,
  Tooltip,
} from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import "../../../styles/Main.scss";

const MOCK_INVENTORY_ITEMS = [
  {
    key: "ITEM-TAP-001",
    itemName: "Basin Mixer Tap",
    uom: "Nos",
    systemExpectedQty: 10,
    physicalCount: null,
  },
  {
    key: "ITEM-CEM-050",
    itemName: "Cement Bag 50kg",
    uom: "Bags",
    systemExpectedQty: 150,
    physicalCount: null,
  },
  {
    key: "ITEM-PVC-2IN",
    itemName: "PVC Pipe 2 inch",
    uom: "Nos",
    systemExpectedQty: 65,
    physicalCount: null,
  },
  {
    key: "ITEM-PIPE-4IN",
    itemName: "PVC Pipe 4 inch",
    uom: "Nos",
    systemExpectedQty: 45,
    physicalCount: null,
  },
  {
    key: "ITEM-WIRE-12",
    itemName: "Electrical Wire 12 AWG",
    uom: "Meters",
    systemExpectedQty: 200,
    physicalCount: null,
  },
];

const PhysicalInventoryVerification = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(MOCK_INVENTORY_ITEMS);

  const updatePhysicalCount = (key, value) => {
    setItems((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, physicalCount: value === null ? null : Math.max(0, value) } : row
      )
    );
  };

  const rowsWithVariance = useMemo(() => {
    return items.map((row) => {
      const physicalCount = row.physicalCount ?? null;
      let variance = null;
      let status = "pending";

      if (physicalCount !== null) {
        variance = physicalCount - row.systemExpectedQty;
        if (variance === 0) {
          status = "matched";
        } else {
          status = "mismatch";
        }
      }

      return { ...row, variance, status };
    });
  }, [items]);

  const disableSubmit = useMemo(() => {
    return !rowsWithVariance.some((row) => row.physicalCount !== null);
  }, [rowsWithVariance]);

  const handleFinalize = () => {
    const payload = rowsWithVariance.map(
      ({ key, itemName, uom, systemExpectedQty, physicalCount, variance, status }) => ({
        itemKey: key,
        itemName,
        uom,
        systemExpectedQty,
        physicalCount,
        variance,
        status,
      })
    );
    // eslint-disable-next-line no-console
    console.log("Finalize & Submit Count", payload);

    const varianceRows = payload
      .filter((r) => r.physicalCount !== null && r.variance !== 0)
      .map((r) => ({
        key: r.itemKey,
        item: r.itemName,
        expected: r.systemExpectedQty,
        physical: r.physicalCount,
        variance: r.variance,
        userNotes:
          r.variance < 0
            ? "Shortage observed during count. Please verify rack/backstore."
            : "Excess found during count. No PO tag identified.",
      }));

    navigate("/physical-material-variance-summary", {
      state: {
        verificationRef: "#VER-GOTA-NOV25",
        totalItems: 150,
        mismatches: varianceRows.length,
        varianceRows,
      },
    });
  };

  const columns = [
    {
      title: "Item Name (UOM)",
      dataIndex: "itemName",
      key: "itemName",
      render: (_, record) => (
        <div>
          <div>{record.itemName}</div>
          <div className="muted-text">({record.uom})</div>
        </div>
      ),
      onCell: (record) => ({
        className: record.status === "mismatch" ? "warning-row" : "",
      }),
    },
    {
      title: "System Expected Qty (Snapshot)",
      dataIndex: "systemExpectedQty",
      key: "systemExpectedQty",
      width: 200,
      render: (value) => value.toLocaleString(),
    },
    {
      title: "Physical Count (Enter Actual)",
      key: "physicalCount",
      width: 200,
      render: (_, record) => (
        <InputNumber
          min={0}
          value={record.physicalCount}
          onChange={(value) => updatePhysicalCount(record.key, value)}
          placeholder="Enter count"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Variance / Status",
      key: "variance",
      width: 200,
      render: (_, record) => {
        if (record.status === "pending") {
          return <Tag>Pending</Tag>;
        }
        if (record.status === "matched") {
          return (
            <Space>
              <CheckCircleFilled style={{ color: "#52c41a" }} />
              <span>0 (Matched)</span>
            </Space>
          );
        }
        return (
          <Space>
            <ExclamationCircleFilled style={{ color: "#faad14" }} />
            <span>
              {record.variance > 0 ? "+" : ""}
              {record.variance} (Mismatch)
            </span>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="Perform physical stock counting and submit variance results">
              <h3>Material Inventory – Physical Verification</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Material Indent</Breadcrumb.Item>
              <Breadcrumb.Item>Physical Verification</Breadcrumb.Item>
            </Breadcrumb>
           
          </div>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
        <div className="po-good-header">
            <div className="po-header-details" style={{ margin: "0" }}>
              <div className="po-row">
                <strong>Location:</strong>
                <span>Gota Site Store</span>
              </div>
              <div className="po-row">
                <strong>Verification Date:</strong>
                <span>{new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}</span>
              </div>
            </div>
            </div>
            <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: 0,marginTop: 0, alignItems:'flex-start' }}>
                  <div className="returninfo-title">Active Counting Interface (User Entry)</div>
                </Divider>
          
          <div className="tablewrp" style={{  marginTop: '10px', marginBottom: '24px' }}>
            <Table
              rowKey="key"
              columns={columns}
              dataSource={rowsWithVariance}
              pagination={false}
              scroll={{ x: 800 }}
            />
          </div>

          <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              className="SaveGradiantBtn"
              disabled={disableSubmit}
              onClick={handleFinalize}
            >
              Finalize &amp; Submit Count
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default PhysicalInventoryVerification;
